import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts'
import {
  formatCompactCurrencyBrl,
  formatFullCurrencyBrl,
} from '../../lib/managerial-kpis'
import type { RevenueOriginBreakdown } from '../../lib/revenue-origin'
import styles from './RevenueOriginChart.module.css'

interface RevenueOriginChartProps {
  breakdown: RevenueOriginBreakdown | null
  isLoading: boolean
}

export function RevenueOriginChart({ breakdown, isLoading }: RevenueOriginChartProps) {
  return (
    <section
      className={styles.card}
      aria-labelledby="revenue-origin-title"
      aria-busy={isLoading}
    >
      <header className={styles.header}>
        <h2 id="revenue-origin-title" className={styles.title}>
          GMV por origem
        </h2>
        {breakdown ? (
          <p className={styles.total}>Total: {breakdown.formattedTotal}</p>
        ) : null}
      </header>
      <p className={styles.criteria}>
        Último contato registrado antes do pagamento. Prioridade: afiliado, anúncio,
        indicação, direto/orgânico. Sem evidência suficiente: não identificado.
      </p>
      {breakdown === null ? (
        <p className={styles.empty}>{isLoading ? 'Carregando origens...' : '—'}</p>
      ) : breakdown.total === 0 ? (
        <p className={styles.empty}>Nenhuma receita neste período.</p>
      ) : (
        <div className={styles.body}>
          <div className={styles.donutWrap}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={breakdown.slices.filter((slice) => slice.amount > 0)}
                  dataKey="amount"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  innerRadius="68%"
                  outerRadius="92%"
                  paddingAngle={2}
                  stroke="#fff"
                  strokeWidth={3}
                  startAngle={90}
                  endAngle={-270}
                >
                  {breakdown.slices
                    .filter((slice) => slice.amount > 0)
                    .map((slice) => (
                      <Cell key={slice.id} fill={slice.color} />
                    ))}
                </Pie>
                <Tooltip
                  formatter={(value, name) => [
                    formatFullCurrencyBrl(Number(value ?? 0)),
                    String(name ?? ''),
                  ]}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className={styles.centerLabel}>
              <strong className={styles.centerValue}>
                {formatCompactCurrencyBrl(breakdown.total)}
              </strong>
            </div>
          </div>
          <ul className={styles.legend} aria-label="Distribuição da receita por origem">
            {breakdown.slices.map((slice) => (
              <li key={slice.id} className={styles.legendItem}>
                <span
                  className={styles.legendDot}
                  style={{ background: slice.color }}
                  aria-hidden
                />
                <span className={styles.legendName}>{slice.label}</span>
                <span className={styles.legendValue}>
                  {formatFullCurrencyBrl(slice.amount)}
                  <br />
                  {slice.formattedPercent}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
